/**
 * Every scrolling region in Connect's web apps: SimpleBar (`simplebar-react`),
 * the library — not a scrollbar of Connect's own.
 *
 * The browser's native bars differ on every platform (a 17-pixel grey slab on
 * Windows, nothing at all on a Mac until you scroll) and cannot be themed in
 * Firefox beyond a colour. SimpleBar keeps native scrolling — wheel, touch,
 * keyboard, momentum — and draws one slim bar, the same everywhere, in the
 * theme's colours (`tokens.css`, `.simplebar-*`), for both directions.
 *
 * This component only sets Connect's defaults on it:
 *
 * - the region is focusable by keyboard only when it can scroll, as SimpleBar
 *   leaves it;
 * - `viewportRef` / `onViewportScroll` reach the element that actually scrolls
 *   (SimpleBar's content wrapper), for `scrollTop`, listeners and
 *   `scrollIntoView` targets;
 * - `fill` lays the content out as a column at least as tall as the region, so
 *   a page can grow into the space and a footer can sit at its bottom. SimpleBar
 *   sizes its content to the content itself (a region sized by `flex-1` reads to
 *   it as "height auto"), so no stylesheet can say "at least the region's
 *   height": the region is watched and its height given to the content. A page
 *   inside it marked `data-fill-view` (Chats, the flow builder) gets exactly
 *   that height — its own regions scroll, never the page — rather than at
 *   least it, which a tall thread would otherwise stretch.
 *
 * **Why the elements are found from the scrolling element, not SimpleBar's
 * instance.** simplebar-react creates its instance (`ref.current`, with `.el`
 * and `getContentElement()`) in a passive `useEffect`, and React runs every
 * layout effect — this component's included — before any passive one. On a
 * real mount the instance is not there yet, so `fill` did nothing in
 * production; development only worked because StrictMode mounts effects twice.
 * The scrolling element is ours through `scrollableNodeProps.ref`, attached at
 * commit, so the frame and the content are read from the DOM around it.
 *
 * Like any SimpleBar, the region needs a bounded height — `max-h-*`, `h-*`, or
 * `min-h-0 flex-1` inside a flex column — to have anything to scroll. The last
 * works only because `tokens.css` sizes SimpleBar's wrapper to the region
 * (`[data-simplebar] > .simplebar-wrapper`); SimpleBar's own `height: inherit`
 * sees an unsized flex item as "grows with its content".
 */

import { useLayoutEffect, useRef, type HTMLAttributes, type RefObject, type UIEvent } from 'react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

import { cn } from '../../lib/cn';

export interface ScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  /** The element that scrolls. An object ref: SimpleBar reads `.current`, and
   *  never calls a callback ref with the element. */
  viewportRef?: RefObject<HTMLDivElement | null>;
  onViewportScroll?: (event: UIEvent<HTMLDivElement>) => void;
  /** Content as a column at least the region's height. */
  fill?: boolean;
  /** Classes for the content — its padding and spacing. On `className` they
   *  would land on SimpleBar's frame, outside the scrolling content, where
   *  `space-y-*` spaces nothing and padding sits beside the scrollbar. */
  contentClassName?: string;
}

export function ScrollArea({
  viewportRef,
  onViewportScroll,
  fill,
  className,
  contentClassName,
  children,
  ...rest
}: ScrollAreaProps) {
  const scroller = useRef<HTMLDivElement | null>(null);
  // The caller's ref sees the same element. Children's layout effects run
  // first, so it is set before a caller's own effects read it.
  useLayoutEffect(() => {
    if (!viewportRef) return undefined;
    viewportRef.current = scroller.current;
    return () => {
      viewportRef.current = null;
    };
  }, [viewportRef]);
  useLayoutEffect(() => {
    // SimpleBar's markup: [data-simplebar] > … > .simplebar-content-wrapper
    // (the scroller) > .simplebar-content.
    const root = scroller.current?.closest<HTMLElement>('[data-simplebar]');
    const content = scroller.current?.querySelector<HTMLElement>(':scope > .simplebar-content');
    // jsdom has no ResizeObserver; a test then lays out without the minimum.
    if (!fill || !root || !content || typeof ResizeObserver === 'undefined') return undefined;
    const size = () => {
      content.style.minHeight = `${root.clientHeight}px`;
      // For a page that fills the view exactly (tokens.css, [data-fill-view]).
      content.style.setProperty('--scroll-area-height', `${root.clientHeight}px`);
    };
    size();
    const watch = new ResizeObserver(size);
    watch.observe(root);
    return () => {
      watch.disconnect();
      content.style.minHeight = '';
      content.style.removeProperty('--scroll-area-height');
    };
  }, [fill]);
  return (
    <SimpleBar
      {...rest}
      className={cn(fill && 'scroll-area-fill', className)}
      scrollableNodeProps={{ ref: scroller, onScroll: onViewportScroll }}
    >
      {contentClassName ? <div className={contentClassName}>{children}</div> : children}
    </SimpleBar>
  );
}
